import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Sparkle, Crown, UsersThree, Spinner, ArrowRight, XCircle, CheckCircle, ArrowClockwise, Clock } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TIER_META = {
  free: { icon: Sparkle, color: "text-zinc-400", label: "Free" },
  pro: { icon: Crown, color: "text-orange-400", label: "Pro" },
  agency: { icon: UsersThree, color: "text-orange-400", label: "Agency" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function SubscriptionCard() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pricing/subscription");
      setSub(data);
    } catch {
      setSub({ tier: "free", status: "active" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancel = async () => {
    if (!confirm(`Cancel your ${sub.tier} plan? You'll keep access until ${formatDate(sub.current_period_end)}.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pricing/subscription/cancel");
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pricing/subscription/resume");
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 lg:col-span-2" data-testid="subscription-loading">
        <Spinner size={16} className="animate-spin text-zinc-500 mx-auto" />
      </div>
    );
  }

  const meta = TIER_META[sub?.tier] || TIER_META.free;
  const Icon = meta.icon;
  const isFree = sub?.tier === "free";
  const isCanceling = !!sub?.cancel_at_period_end;
  const isTrialing = !!sub?.is_trialing;
  const trialDaysLeft = sub?.trial_days_left || 0;

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 lg:col-span-2" data-testid="subscription-card">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard size={16} weight="duotone" className="text-orange-500" />
        <div className="font-display font-medium text-zinc-100">Subscription</div>
        {isTrialing && (
          <span data-testid="subscription-trial-chip" className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-orange-500/50 bg-orange-500/10 text-orange-300">
            <Clock size={10} weight="fill" /> Trial · {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-[auto_1fr_auto] items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-zinc-950 border border-zinc-800 rounded-sm flex items-center justify-center">
            <Icon size={20} weight="duotone" className={meta.color} />
          </div>
          <div>
            <div className="font-display text-xl font-semibold text-zinc-50" data-testid="subscription-tier-label">{meta.label}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              {isTrialing ? "Trial plan" : "Current plan"}
            </div>
          </div>
        </div>

        <div className="space-y-1 text-sm">
          {isTrialing ? (
            <>
              <div className="flex items-center gap-1.5 text-orange-400">
                <Sparkle size={14} weight="fill" /> Full Pro access
              </div>
              <div className="text-xs text-zinc-500" data-testid="subscription-trial-ends">
                Trial ends <span className="font-mono">{formatDate(sub.trial_ends_at)}</span> — you'll drop to Free unless you upgrade.
              </div>
            </>
          ) : isFree ? (
            <div className="text-zinc-400">You're on the free tier — upgrade for unlimited leads and AI outreach.</div>
          ) : isCanceling ? (
            <>
              <div className="flex items-center gap-1.5 text-amber-400" data-testid="subscription-canceling-banner">
                <XCircle size={14} weight="fill" /> Cancels on {formatDate(sub.current_period_end)}
              </div>
              <div className="text-xs text-zinc-500">You'll keep {meta.label} features until then, then drop to Free.</div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1.5 text-emerald-400">
                <CheckCircle size={14} weight="fill" /> Active
              </div>
              <div className="text-xs text-zinc-500" data-testid="subscription-next-bill">
                Next bill: <span className="font-mono">{formatDate(sub.current_period_end)}</span>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          {isTrialing ? (
            <Link to="/pricing" data-testid="subscription-upgrade-link">
              <Button className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm">
                Upgrade now <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </Link>
          ) : isFree ? (
            <Link to="/pricing" data-testid="subscription-upgrade-link">
              <Button className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm">
                Upgrade <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </Link>
          ) : isCanceling ? (
            <Button
              onClick={resume}
              disabled={busy}
              data-testid="subscription-resume-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-zinc-950 rounded-sm"
            >
              {busy ? <Spinner size={14} className="animate-spin" /> : <><ArrowClockwise size={14} className="mr-1.5" /> Resume</>}
            </Button>
          ) : (
            <>
              <Link to="/pricing" data-testid="subscription-change-link">
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">
                  Change plan
                </Button>
              </Link>
              <Button
                onClick={cancel}
                disabled={busy}
                data-testid="subscription-cancel-btn"
                variant="outline"
                className="border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm"
              >
                {busy ? <Spinner size={14} className="animate-spin" /> : "Cancel"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
